import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { Folder, FolderPlus, PencilSimple, Trash } from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";

export type FolderSelection = "all" | "unfiled" | number;
interface PhotoFolder {
  id: number;
  name: string;
  photo_count?: number;
  document_count?: number;
}
interface Props {
  kind?: "photos" | "documents";
  parent: { job?: number; opportunity?: number };
  active: FolderSelection;
  onSelect: (folder: FolderSelection) => void;
  selectedIds: Set<number>;
  photos: { id: number; folder?: number | null }[];
  onChanged: () => void;
}
export function PhotoFolderToolbar({
  kind = "photos",
  parent,
  active,
  onSelect,
  selectedIds,
  photos,
  onChanged,
}: Props) {
  const client = useQueryClient();
  const resource = kind === "photos" ? "photo-folders" : "document-folders";
  const key = [resource, parent.job ?? null, parent.opportunity ?? null];
  const scope = parent.job
    ? `job=${parent.job}`
    : `opportunity=${parent.opportunity}`;
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      (await apiClient.get<PhotoFolder[]>(`/api/${resource}/?${scope}`)).data,
  });
  const folders = query.data || [];
  const photoVersion = photos
    .map((photo) => `${photo.id}:${photo.folder ?? ""}`)
    .join(",");
  useEffect(() => {
    void client.invalidateQueries({
      queryKey: [
        resource,
        parent.job ?? null,
        parent.opportunity ?? null,
      ],
    });
  }, [client, parent.job, parent.opportunity, photoVersion, resource]);
  const current = folders.find((f) => f.id === active);
  const [dialog, setDialog] = useState<"create" | "rename" | "delete" | null>(
    null,
  );
  const [name, setName] = useState("");
  const [destination, setDestination] = useState<FolderSelection>("unfiled");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function change(action: () => Promise<unknown>) {
    setBusy(true);
    setError("");
    try {
      await action();
      setDialog(null);
      await client.invalidateQueries({ queryKey: key });
      onChanged();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not update folders. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Box sx={{ mb: 2 }}>
      {(error || query.isError) && (
        <Alert color="danger" sx={{ mb: 1 }}>
          {error || `Could not load ${kind} folders. Refresh to try again.`}
        </Alert>
      )}
      <Stack
        direction="row"
        gap={1}
        flexWrap="wrap"
        alignItems="center"
        aria-label={kind === "photos" ? "Photo folders" : "Document folders"}
      >
        <Button
          size="sm"
          variant={active === "all" ? "solid" : "soft"}
          onClick={() => onSelect("all")}
        >
          All {kind} ({photos.length})
        </Button>
        <Button
          size="sm"
          variant={active === "unfiled" ? "solid" : "plain"}
          onClick={() => onSelect("unfiled")}
        >
          Unfiled ({photos.filter((p) => !p.folder).length})
        </Button>
        {folders.map((f) => (
          <Button
            key={f.id}
            size="sm"
            startDecorator={<Folder />}
            variant={active === f.id ? "solid" : "outlined"}
            onClick={() => onSelect(f.id)}
          >
            {f.name} ({kind === "photos" ? f.photo_count : f.document_count})
          </Button>
        ))}
        <Button
          size="sm"
          variant="plain"
          startDecorator={<FolderPlus />}
          onClick={() => {
            setName("");
            setError("");
            setDialog("create");
          }}
        >
          New folder
        </Button>
        {current && (
          <>
            <Button
              size="sm"
              variant="plain"
              aria-label={`Rename ${current.name}`}
              onClick={() => {
                setName(current.name);
                setError("");
                setDialog("rename");
              }}
            >
              <PencilSimple />
            </Button>
            <Button
              size="sm"
              color="danger"
              variant="plain"
              aria-label={`Remove ${current.name} folder`}
              onClick={() => {
                setError("");
                setDialog("delete");
              }}
            >
              <Trash />
            </Button>
          </>
        )}
      </Stack>
      {selectedIds.size > 0 && (
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          alignItems="center"
          sx={{ mt: 1.5 }}
        >
          <Typography level="body-sm">
            Move {selectedIds.size} selected to
          </Typography>
          <Select<FolderSelection>
            aria-label="Destination folder"
            size="sm"
            value={destination}
            onChange={(_, value) => {
              if (value !== null) setDestination(value);
            }}
          >
            <Option value="unfiled">Unfiled</Option>
            {folders.map((f) => (
              <Option value={f.id} key={f.id}>
                {f.name}
              </Option>
            ))}
          </Select>
          <Button
            size="sm"
            loading={busy}
            onClick={() =>
              void change(async () => {
                const ids = [...selectedIds];
                for (let offset = 0; offset < ids.length; offset += 500) {
                  await apiClient.post(`/api/${resource}/move-${kind}/`, {
                    ...parent,
                    [kind === "photos" ? "photo_ids" : "document_ids"]: ids.slice(offset, offset + 500),
                    folder: typeof destination === "number" ? destination : null,
                  });
                }
              })
            }
          >
            Move {kind}
          </Button>
        </Stack>
      )}
      <Modal
        open={dialog !== null}
        onClose={() => {
          if (!busy) setDialog(null);
        }}
      >
        <ModalDialog sx={{ width: 420, maxWidth: "calc(100vw - 32px)" }}>
          <Typography level="h4">
            {dialog === "create"
              ? `New ${kind === "photos" ? "photo" : "document"} folder`
              : dialog === "rename"
                ? "Rename folder"
                : "Remove folder?"}
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          {dialog === "delete" ? (
            <Typography>
              Photos in “{current?.name}” will return to Unfiled. Your photos
              will be kept.
            </Typography>
          ) : (
            <FormControl>
              <FormLabel>Folder name</FormLabel>
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                slotProps={{ input: { maxLength: 100 } }}
                onKeyDown={(e) => {
                  if (e.key === "Enter")
                    document.getElementById("save-photo-folder")?.click();
                }}
              />
            </FormControl>
          )}
          <Stack direction="row" gap={1} justifyContent="flex-end">
            <Button
              variant="plain"
              disabled={busy}
              onClick={() => setDialog(null)}
            >
              Cancel
            </Button>
            <Button
              id="save-photo-folder"
              color={dialog === "delete" ? "danger" : "primary"}
              loading={busy}
              disabled={dialog !== "delete" && !name.trim()}
              onClick={() =>
                void change(async () => {
                  if (dialog === "create") {
                    const result = await apiClient.post<PhotoFolder>(
                      `/api/${resource}/`,
                      { ...parent, name: name.trim() },
                    );
                    onSelect(result.data.id);
                  } else if (dialog === "rename" && current)
                    await apiClient.patch(
                      `/api/${resource}/${current.id}/?${scope}`,
                      { name: name.trim() },
                    );
                  else if (dialog === "delete" && current) {
                    await apiClient.delete(
                      `/api/${resource}/${current.id}/?${scope}`,
                    );
                    onSelect("unfiled");
                  }
                })
              }
            >
              {dialog === "delete" ? "Remove folder" : "Save folder"}
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
